import {
  BadRequestException,
  Body,
  Controller,
  Delete,
  Get,
  Param,
  Patch,
  Post,
  Query,
  Req,
  UseGuards,
} from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { PrismaService } from '@cart/api-data-access';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { BackupService } from './backup.service';
import { BackupQuotaService } from './backup-quota.service';
import { BackupAlertService } from './backup-alert.service';
import { RecoveryWindowService } from './recovery-window.service';
import { RestoreService } from './restore.service';
import { WalMonitorService } from './wal-monitor.service';
import { TimeTravelService } from './time-travel.service';
import { MerchantSnapshotService } from './merchant-snapshot.service';
import { RetentionService } from './retention.service';
import { LocalBackupService } from './local-backup.service';
import {
  backupEnvPrefix,
  backupBucket,
  readBackupStorageEnv,
  dumpPrefix,
  walFileName,
  walPrefix,
} from './backup-storage.constants';

/**
 * Platform-admin database maintenance.
 *
 * What an admin can do here: see whether we are recoverable and to when, run a
 * backup, change a schedule, request a sandbox restore, run a Time Travel undo
 * on a merchant's behalf, and drive routine maintenance.
 *
 * What an admin cannot do here, at all: read a repository credential. There is
 * no endpoint for it and no code path to one — repositories carry a
 * `credentialRef` naming an environment key, never a value, the same policy
 * already enforced in PlatformStorageController. Nor can anyone promote a
 * restore over production from a web request; see RestoreService.promote.
 */
@ApiTags('Platform · Database')
@ApiBearerAuth()
@Controller('platform/database')
@UseGuards(AuthGuard, DemoModeGuard)
export class DatabaseController {
  constructor(
    private readonly prisma: PrismaService,
    private readonly backups: BackupService,
    private readonly quota: BackupQuotaService,
    private readonly alerts: BackupAlertService,
    private readonly window: RecoveryWindowService,
    private readonly restores: RestoreService,
    private readonly wal: WalMonitorService,
    private readonly timeTravel: TimeTravelService,
    private readonly snapshots: MerchantSnapshotService,
    private readonly local: LocalBackupService,
    private readonly retentionService: RetentionService
  ) {}

  private actor(req: GuardedRequest): string {
    return req.user?.id ?? req.user?.email ?? 'system';
  }

  /**
   * Recursively turn BigInt into string for JSON.
   *
   * `JSON.stringify` throws outright on a BigInt — "Do not know how to
   * serialize a BigInt" — so any endpoint returning a raw Prisma row from a
   * table with a byte counter fails at response time rather than at compile
   * time. Several tables here carry them (sizeBytes, quotaBytes,
   * bytesDownloaded, rowsRestored, estimatedBytes …), which makes
   * hand-mapping every field an ongoing source of exactly this bug.
   *
   * Numbers are not safe: a BigInt column can exceed Number.MAX_SAFE_INTEGER,
   * and silently losing precision on a byte count is worse than a string the
   * client parses. This is scoped to this controller rather than patching
   * BigInt.prototype.toJSON globally, which would change serialisation for
   * every other module in the app.
   */
  private serialize<T>(value: T): T {
    if (typeof value === 'bigint') return value.toString() as unknown as T;
    if (value instanceof Date || value === null || value === undefined) return value;
    if (Array.isArray(value)) return value.map((item) => this.serialize(item)) as unknown as T;
    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
        out[key] = this.serialize(item);
      }
      return out as T;
    }
    return value;
  }

  @Get('overview')
  @ApiOperation({ summary: 'Recoverability, budget, RPO and open alerts in one call' })
  async overview() {
    const targets = await this.prisma.backupTarget.findMany({ where: { isActive: true } });
    const repositories = await this.prisma.backupRepository.findMany({
      where: { isActive: true },
      orderBy: { repoIndex: 'asc' },
    });

    const effective = readBackupStorageEnv();

    const perTarget = await Promise.all(
      targets.map(async (target) => {
        const [window, rpo, lastRun, lastDrill] = await Promise.all([
          this.window.advertisedWindow(target.id),
          this.wal.rpo(target.id),
          this.prisma.backupRun.findFirst({
            where: { targetId: target.id, status: 'completed' },
            orderBy: { startedAt: 'desc' },
          }),
          this.prisma.backupDrill.findFirst({
            where: { targetId: target.id, status: 'passed' },
            orderBy: { startedAt: 'desc' },
          }),
        ]);

        return {
          id: target.id,
          code: target.code,
          name: target.name,
          databases: target.databases,
          sizeBytes: target.sizeBytes.toString(),
          // The honest window, computed from what is actually restorable —
          // never a configured "30 days" nobody checked.
          window: window && {
            start: window.start.toISOString(),
            end: window.end.toISOString(),
            hasGaps: window.hasGaps,
            days: Math.round((window.end.getTime() - window.start.getTime()) / 86400000),
          },
          // Measured, not configured. archive_timeout does not bound this.
          rpo: {
            measuredSeconds: rpo.measuredSeconds,
            pendingSegments: rpo.pendingCount,
            backlogGrowing: rpo.backlogGrowing,
          },
          lastBackupAt: lastRun?.finishedAt?.toISOString() ?? null,
          lastVerifiedRestoreAt: lastDrill?.finishedAt?.toISOString() ?? null,
          lastDrillRtoSeconds: lastDrill?.rtoSeconds ?? null,
        };
      })
    );

    return {
      targets: perTarget,
      repositories: repositories.map((repo) => ({
        id: repo.id,
        code: repo.code,
        name: repo.name,
        kind: repo.kind,
        // EFFECTIVE values, not the seeded configuration.
        //
        // The row still said `forgestack-db-backups` / `R2_BACKUP_WRITE` from
        // migration 058 while everything actually went to bucket `test` using
        // the shared PLATFORM_R2_* credentials. A screen that names a bucket
        // nothing is written to, and a credential that does not exist, is worse
        // than one that shows nothing — it answers "where are my backups?"
        // incorrectly and confidently.
        bucket: effective?.bucket ?? repo.bucket,
        endpoint: effective?.endpoint ?? repo.endpoint,
        credentialRef: effective
          ? effective.sharedWithMedia
            ? 'PLATFORM_R2_* (shared with media)'
            : 'BACKUP_*'
          : repo.credentialRef,
        /** True when the stored config disagrees with what is actually used. */
        configDrift: Boolean(effective && repo.bucket && effective.bucket !== repo.bucket),
        status: repo.status,
        usedBytes: repo.usedBytes.toString(),
        backupBytes: repo.backupBytes.toString(),
        walBytes: repo.walBytes.toString(),
        quotaBytes: repo.quotaBytes.toString(),
        usedPct: repo.quotaBytes > 0n ? Number((repo.usedBytes * 100n) / repo.quotaBytes) : 0,
        softLimitPct: repo.softLimitPct,
        hardLimitPct: repo.hardLimitPct,
        // The local backend gzips; it does not encrypt. Repository encryption is
        // a pgBackRest feature (repo2-cipher-type), and pgBackRest is not what
        // is running here. Claiming AES-256 on a compressed-but-plaintext
        // object is the kind of reassurance that gets believed.
        encrypted: effective ? false : repo.encrypted,
        compressed: true,
        immutable: repo.immutable,
        appendOnly: repo.appendOnly,
        retentionFull: repo.retentionFull,
        retentionDiff: repo.retentionDiff,
        retentionWalDays: repo.retentionWalDays,
        retentionBackupDays: repo.retentionBackupDays,
      })),
      alerts: await this.alerts.open(),
      // What this host can actually do, and where its bytes land. Shown so an
      // operator never has to guess which backend produced a run, or whether
      // the credential separation the design assumes is actually in place.
      storage: {
        environment: backupEnvPrefix(),
        bucket: backupBucket(),
        dumpPrefix: dumpPrefix(),
        walPrefix: walPrefix(),
        logicalBackupAvailable: this.local.isAvailable(),
        logicalBackupBlockedBy: this.local.unavailableReason(),
        // One key for media and backups means the media key can delete backup
        // history. Saying so beats a screen that implies an isolation the
        // deployment does not have.
        sharesMediaCredentials: this.local.sharesMediaCredentials(),
      },
    };
  }

  @Get('recovery-window')
  @ApiOperation({ summary: 'Restorable intervals and any gaps — powers the timeline' })
  async recoveryWindow(@Query('targetId') targetId: string) {
    const points = await this.prisma.recoveryPoint.findMany({
      where: { targetId },
      orderBy: { windowStart: 'asc' },
    });

    const backups = await this.prisma.backupRun.findMany({
      where: { targetId, status: 'completed' },
      orderBy: { startedAt: 'asc' },
      select: { id: true, backupType: true, startedAt: true, finishedAt: true, repoSizeBytes: true },
    });

    // Migration markers, so an operator can SEE they are about to restore
    // across a schema boundary rather than discovering it afterwards.
    const migrations = await this.prisma.schemaMigration.findMany({
      where: { database: 'shopora' },
      orderBy: { sequence: 'asc' },
      select: { filename: true, appliedAt: true, forwardSafe: true },
    });

    return {
      intervals: points.map((p) => ({
        timeline: p.timeline,
        start: p.windowStart.toISOString(),
        end: p.windowEnd.toISOString(),
        segmentCount: p.segmentCount,
        isCurrent: p.isCurrent,
        continuity: p.continuity,
      })),
      backups: backups.map((b) => ({
        id: b.id,
        type: b.backupType,
        at: (b.finishedAt ?? b.startedAt).toISOString(),
        repoSizeBytes: b.repoSizeBytes.toString(),
      })),
      migrations: migrations.map((m) => ({
        filename: m.filename,
        at: m.appliedAt.toISOString(),
        forwardSafe: m.forwardSafe,
      })),
    };
  }

  @Get('runs')
  @ApiOperation({ summary: 'Backup run history' })
  async runs(@Query('targetId') targetId?: string, @Query('limit') limit?: string) {
    const runs = await this.prisma.backupRun.findMany({
      where: targetId ? { targetId } : {},
      orderBy: { startedAt: 'desc' },
      take: Math.min(Number(limit) || 50, 200),
      include: { policy: { select: { name: true } }, repository: { select: { code: true } } },
    });

    return runs.map((run) => ({
      id: run.id,
      type: run.backupType,
      trigger: run.trigger,
      status: run.status,
      policy: run.policy?.name ?? null,
      repository: run.repository?.code ?? null,
      label: run.label,
      // Split so the table can show WHERE separately from WHAT — a single long
      // key in one cell is unreadable, and the bucket is the part that answers
      // "which environment did this go to?".
      bucket: backupBucket(),
      storagePath: run.manifestKey,
      sizeBytes: run.sizeBytes.toString(),
      repoSizeBytes: run.repoSizeBytes.toString(),
      schemaVersion: run.schemaVersion,
      verifyStatus: run.verifyStatus,
      startedAt: run.startedAt.toISOString(),
      finishedAt: run.finishedAt?.toISOString() ?? null,
      durationMs: run.durationMs,
      error: run.error,
    }));
  }

  @Get('base-backups')
  @ApiOperation({ summary: 'Physical base backups available as PITR starting positions' })
  async baseBackups() {
    return this.backups.listBaseBackups();
  }

  @Get('pitr/plan')
  @ApiOperation({ summary: 'What a restore to this instant would use — computes nothing else' })
  async pitrPlan(@Query('targetTime') targetTime: string) {
    const at = new Date(targetTime ?? '');
    if (Number.isNaN(at.getTime())) {
      throw new BadRequestException('targetTime must be a valid timestamp (include the offset).');
    }
    return this.backups.planPointInTime(at);
  }

  @Post('pitr/restore')
  @ApiOperation({ summary: 'Restore a base backup and replay WAL to a chosen instant' })
  async pitrRestore(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    const targetTime = new Date(String(body['targetTime'] ?? ''));
    if (Number.isNaN(targetTime.getTime())) {
      throw new BadRequestException('targetTime must be a valid timestamp (include the offset).');
    }
    if (!String(body['reason'] ?? '').trim()) {
      throw new BadRequestException('A reason is required.');
    }

    return this.backups.restoreToPointInTime(
      {
        targetTime,
        basePrefix: body['basePrefix'] ? String(body['basePrefix']) : undefined,
        reason: String(body['reason']).trim(),
      },
      this.actor(req)
    );
  }

  @Post('pitr/:id/teardown')
  @ApiOperation({ summary: 'Stop a PITR sandbox and delete its data directory' })
  async pitrTeardown(@Param('id') id: string) {
    return this.backups.teardownPitr(id);
  }

  @Post('runs/:id/restore')
  @ApiOperation({ summary: 'Restore a logical backup into a scratch database and verify it' })
  async restoreRun(@Param('id') id: string, @Req() req: GuardedRequest) {
    return this.backups.restoreRun(id, this.actor(req));
  }

  @Delete('runs/:id')
  @ApiOperation({ summary: 'Delete a logical backup object from the store' })
  async deleteRun(
    @Param('id') id: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    // An explicit confirmation in the body, not just the HTTP verb. A stray
    // DELETE — a double-click, a replayed request, a curl from history —
    // should not be able to remove a backup on its own.
    if (body?.['confirm'] !== true) {
      throw new BadRequestException('Deleting a backup requires { "confirm": true }.');
    }
    return this.backups.deleteRun(id, this.actor(req));
  }

  @Get('policies')
  async policies() {
    const policies = await this.prisma.backupPolicy.findMany({
      where: { deletedAt: null },
      orderBy: { sortOrder: 'asc' },
    });

    // Whether THIS host can run each one, so the screen can disable the button
    // rather than offering one that always refuses.
    return this.serialize(
      policies.map((policy) => ({
        ...policy,
        ...this.backups.supportFor(policy.backupType),
      }))
    );
  }

  @Patch('policies/:id')
  @ApiOperation({ summary: 'Change a schedule or retention — no deploy required' })
  async updatePolicy(
    @Param('id') id: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    const allowed = [
      'name',
      'cronExpression',
      'timezone',
      'windowMinutes',
      'retentionWalDays',
      'retentionBackupDays',
      'skipIfRunning',
      'slaHours',
      'isEnabled',
    ];
    const data: Record<string, unknown> = {};
    for (const key of allowed) if (key in body) data[key] = body[key];

    if (typeof data['cronExpression'] === 'string') {
      const next = this.backups.nextRun(data['cronExpression']);
      if (!next) throw new BadRequestException('Invalid cron expression (expected 5 fields)');
      data['nextRunAt'] = next;
    }

    data['updatedBy'] = this.actor(req);
    return this.serialize(await this.prisma.backupPolicy.update({ where: { id }, data }));
  }

  @Post('policies/:id/run-now')
  @ApiOperation({ summary: 'Run a policy now; returns as soon as it is under way' })
  async runNow(@Param('id') id: string, @Req() req: GuardedRequest) {
    // A base backup takes 19–44s. Holding an HTTP request open for that is
    // wrong on its own — it pins a connection, risks a proxy timeout, and the
    // caller can do nothing meanwhile.
    //
    // But returning instantly would throw away the fast, useful answers: a
    // type refusal, a quota rejection, "already running". Those settle in
    // milliseconds and are exactly what the operator needs to read.
    //
    // So: race. If it finishes quickly, return the real outcome. If it is still
    // going, hand back a "started" and let the run row carry the rest — the
    // screen already reloads Run history.
    const running = this.backups.runPolicy(id, 'manual', this.actor(req));

    // Attached immediately so a later failure can never surface as an
    // unhandled rejection; the run row records it either way.
    running.catch(() => undefined);

    const outcome = await Promise.race([
      running.then((result) => ({ settled: true as const, result })),
      new Promise<{ settled: false }>((resolve) =>
        setTimeout(() => resolve({ settled: false }), 3000)
      ),
    ]);

    if (outcome.settled) return outcome.result;

    // `started` distinguishes "under way" from "nothing ran". Both have a null
    // runId, but one is progress and the other is a refusal, and showing them
    // the same way is how a refusal gets read as a success.
    return {
      runId: null,
      started: true,
      message: 'Backup started — it is running in the background. Watch Run history for the result.',
    };
  }

  @Get('repositories')
  async repositories() {
    const repos = await this.prisma.backupRepository.findMany({ orderBy: { repoIndex: 'asc' } });
    // Note what is absent: no credential, no secret, not even a masked one.
    return repos.map((repo) => ({
      id: repo.id,
      code: repo.code,
      name: repo.name,
      kind: repo.kind,
      repoIndex: repo.repoIndex,
      bucket: repo.bucket,
      endpoint: repo.endpoint,
      credentialRef: repo.credentialRef,
      encrypted: repo.encrypted,
      immutable: repo.immutable,
      appendOnly: repo.appendOnly,
      retentionFull: repo.retentionFull,
      retentionDiff: repo.retentionDiff,
      retentionDays: repo.retentionDays,
      quotaBytes: repo.quotaBytes.toString(),
      usedBytes: repo.usedBytes.toString(),
      softLimitPct: repo.softLimitPct,
      hardLimitPct: repo.hardLimitPct,
      status: repo.status,
      lastCheckedAt: repo.lastCheckedAt?.toISOString() ?? null,
      lastError: repo.lastError,
    }));
  }

  @Patch('repositories/:id')
  async updateRepository(
    @Param('id') id: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    // `credentialRef` is editable (it names an env key); the credential itself
    // is not accepted here under any field name.
    const allowed = [
      'name',
      'bucket',
      'endpoint',
      'credentialRef',
      'retentionFull',
      'retentionDiff',
      'retentionDays',
      'quotaBytes',
      'softLimitPct',
      'hardLimitPct',
      'immutable',
      'appendOnly',
    ];
    const data: Record<string, unknown> = {};
    for (const key of allowed) if (key in body) data[key] = body[key];

    if ('quotaBytes' in data) data['quotaBytes'] = BigInt(String(data['quotaBytes']));

    // A zero here would delete everything on the next sweep. The database has
    // the same CHECK, but failing in the API gives the operator a sentence
    // instead of a constraint-violation stack.
    for (const key of ['retentionWalDays', 'retentionBackupDays']) {
      if (key in data && Number(data[key]) < 1) {
        throw new BadRequestException(`${key} must be at least 1 day.`);
      }
    }

    const soft = Number(data['softLimitPct'] ?? 0);
    const hard = Number(data['hardLimitPct'] ?? 100);
    if ('softLimitPct' in data && 'hardLimitPct' in data && soft >= hard) {
      throw new BadRequestException(
        'softLimitPct must be below hardLimitPct — otherwise the warning never fires before the refusal.'
      );
    }

    data['updatedBy'] = this.actor(req);
    return this.serialize(await this.prisma.backupRepository.update({ where: { id }, data }));
  }

  @Post('repositories/:id/refresh')
  async refreshRepository(@Param('id') id: string) {
    const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
    if (target) await this.quota.refresh(target.stanza);
    return this.prisma.backupRepository.findUnique({ where: { id } }).then((r) => ({
      usedBytes: r?.usedBytes.toString(),
      status: r?.status,
    }));
  }

  @Get('retention')
  @ApiOperation({ summary: 'Retention settings and what they actually guarantee' })
  async retention() {
    const repositories = await this.prisma.backupRepository.findMany({
      where: { isActive: true },
      orderBy: { repoIndex: 'asc' },
    });

    return Promise.all(
      repositories.map(async (repo) => ({
        id: repo.id,
        code: repo.code,
        name: repo.name,
        kind: repo.kind,
        retentionWalDays: repo.retentionWalDays,
        retentionBackupDays: repo.retentionBackupDays,
        retentionFull: repo.retentionFull,
        quotaBytes: repo.quotaBytes.toString(),
        usedBytes: repo.usedBytes.toString(),
        // Measured from what the store HOLDS, never from the configured
        // numbers. A retention setting is an intention; this is the outcome,
        // and they come apart the moment a backup fails.
        guarantee: await this.retentionService.guarantee(repo.id),
      }))
    );
  }

  @Get('health')
  async health(@Query('targetId') targetId: string, @Query('hours') hours?: string) {
    const since = new Date(Date.now() - (Number(hours) || 48) * 60 * 60 * 1000);
    const snapshots = await this.prisma.databaseHealthSnapshot.findMany({
      where: { targetId, collectedAt: { gte: since } },
      orderBy: { collectedAt: 'asc' },
    });

    return snapshots.map((s) => ({
      at: s.collectedAt.toISOString(),
      databaseName: s.databaseName,
      databaseBytes: s.databaseBytes.toString(),
      connectionCount: s.connectionCount,
      maxConnections: s.maxConnections,
      cacheHitRatio: s.cacheHitRatio,
      deadTupleCount: s.deadTupleCount.toString(),
      diskFreeBytes: s.diskFreeBytes.toString(),
      restoreHeadroomOk: s.restoreHeadroomOk,
      status: s.status,
    }));
  }

  @Get('wal/history')
  @ApiOperation({ summary: 'Archived WAL segments, paginated' })
  async walHistory(
    @Query('targetId') targetId: string,
    @Query('page') page?: string,
    @Query('pageSize') pageSize?: string
  ) {
    // Capped: a busy cluster archives thousands of segments, and an unbounded
    // page would be a slow query serving a table nobody can read anyway.
    const size = Math.min(Math.max(Number(pageSize) || 20, 1), 100);
    const current = Math.max(Number(page) || 1, 1);

    const where = targetId ? { targetId } : {};
    const [total, rows] = await Promise.all([
      this.prisma.walArchiveSegment.count({ where }),
      this.prisma.walArchiveSegment.findMany({
        where,
        // Newest first: the recent end is what anyone is looking for.
        orderBy: [{ timeline: 'desc' }, { segmentName: 'desc' }],
        skip: (current - 1) * size,
        take: size,
      }),
    ]);

    return {
      page: current,
      pageSize: size,
      total,
      totalPages: Math.max(Math.ceil(total / size), 1),
      bucket: backupBucket(),
      segments: rows.map((row) => ({
        name: row.segmentName,
        timeline: row.timeline,
        status: row.status,
        // Raw is always 16 MB; stored is what the budget pays for. Showing both
        // is what makes the compression visible rather than folklore.
        sizeBytes: row.sizeBytes,
        compressedBytes: row.compressedBytes,
        lagSeconds: row.lagSeconds,
        archivedAt: row.archivedAt?.toISOString() ?? null,
        uploadedAt: row.uploadedAt?.toISOString() ?? null,
        storagePath: `${walPrefix()}/${walFileName(row.segmentName)}`,
        error: row.error,
      })),
    };
  }

  @Get('wal')
  async walStatus(@Query('targetId') targetId: string) {
    const [rpo, recent] = await Promise.all([
      this.wal.rpo(targetId),
      this.prisma.walArchiveSegment.findMany({
        where: { targetId },
        orderBy: { createdAt: 'desc' },
        take: 200,
        select: { segmentName: true, timeline: true, status: true, lagSeconds: true, uploadedAt: true },
      }),
    ]);

    return {
      rpo,
      // Why there is no WAL, when there is none.
      //
      // The screen used to infer this from a segment count of zero and state
      // flatly that no WAL was "expected on this host" — a claim about the
      // environment derived from a number that cannot support it. On a server
      // where archiving had just been switched on it was confidently wrong, and
      // pointed at the Windows config file. Ask the host instead.
      archiving: {
        blockedReason: this.local.walArchivingBlockedReason(),
        source: process.env.BACKUP_WAL_SOURCE ?? null,
      },
      segments: recent.map((s) => ({
        name: s.segmentName,
        timeline: s.timeline,
        status: s.status,
        lagSeconds: s.lagSeconds,
        uploadedAt: s.uploadedAt?.toISOString() ?? null,
      })),
    };
  }

  // ── Restore ────────────────────────────────────────────────────────────────

  @Post('restores')
  async requestRestore(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    return this.restores.request({
      targetId: String(body['targetId']),
      scope: body['scope'] as 'cluster' | 'database' | 'merchant' | 'table' | 'rows',
      databaseName: body['databaseName'] ? String(body['databaseName']) : undefined,
      organizationId: body['organizationId'] ? String(body['organizationId']) : undefined,
      targetTime: body['targetTime'] ? new Date(String(body['targetTime'])) : undefined,
      reason: String(body['reason'] ?? ''),
      requestedBy: this.actor(req),
      requestedByEmail: req.user?.email,
    });
  }

  @Get('restores')
  async listRestores(@Query('limit') limit?: string) {
    return this.serialize(
      await this.prisma.restoreJob.findMany({
        orderBy: { createdAt: 'desc' },
        take: Math.min(Number(limit) || 25, 100),
        include: { verifications: true },
      })
    );
  }

  @Get('restores/:id')
  async getRestore(@Param('id') id: string) {
    return this.serialize(
      await this.prisma.restoreJob.findUnique({ where: { id }, include: { verifications: true } })
    );
  }

  @Post('restores/:id/approve')
  async approveRestore(@Param('id') id: string, @Req() req: GuardedRequest) {
    await this.restores.approve(id, this.actor(req));
    return { approved: true };
  }

  @Post('restores/:id/promote')
  async promoteRestore(@Param('id') id: string, @Req() req: GuardedRequest) {
    await this.restores.promote(id, this.actor(req));
    return { promoted: true };
  }

  // ── Time Travel ────────────────────────────────────────────────────────────

  @Post('time-travel/preview')
  async previewTimeTravel(@Body() body: Record<string, unknown>, @Req() req: GuardedRequest) {
    return this.timeTravel.preview({
      organizationId: String(body['organizationId']),
      storeId: body['storeId'] ? String(body['storeId']) : undefined,
      scope: body['scope'] as 'row' | 'entity' | 'bulk_operation' | 'table' | 'store',
      entityType: body['entityType'] ? String(body['entityType']) : undefined,
      entityIds: (body['entityIds'] as string[]) ?? [],
      correlationId: body['correlationId'] ? String(body['correlationId']) : undefined,
      restoreToAt: new Date(String(body['restoreToAt'])),
      requestedBy: this.actor(req),
      requestedByEmail: req.user?.email,
    });
  }

  @Post('time-travel/:id/apply')
  async applyTimeTravel(@Param('id') id: string, @Req() req: GuardedRequest) {
    return this.timeTravel.apply(id, this.actor(req));
  }

  @Get('time-travel')
  async listTimeTravel(@Query('organizationId') organizationId?: string) {
    return this.serialize(
      await this.prisma.timeTravelOperation.findMany({
        where: organizationId ? { organizationId } : {},
        orderBy: { createdAt: 'desc' },
        take: 50,
      })
    );
  }

  // ── Snapshots and checkpoints ──────────────────────────────────────────────

  @Get('snapshots')
  async listSnapshots(@Query('organizationId') organizationId?: string) {
    return this.serialize(
      await this.prisma.merchantSnapshot.findMany({
        where: organizationId ? { organizationId } : {},
        orderBy: { snapshotAt: 'desc' },
        take: 50,
      })
    );
  }

  @Post('snapshots')
  async createSnapshot(@Body() body: Record<string, unknown>) {
    return this.snapshots.create({
      organizationId: String(body['organizationId']),
      storeId: body['storeId'] ? String(body['storeId']) : undefined,
      trigger: 'manual',
      admissionReason: 'explicit_request',
    });
  }

  @Get('checkpoints')
  async listCheckpoints(@Query('organizationId') organizationId: string) {
    return this.serialize(
      await this.prisma.bulkOperationCheckpoint.findMany({
        where: { organizationId },
        orderBy: { startedAt: 'desc' },
        take: 50,
      })
    );
  }

  // ── Alerts ─────────────────────────────────────────────────────────────────

  @Get('alerts')
  async listAlerts() {
    return this.serialize(await this.alerts.open());
  }

  @Post('alerts/:id/acknowledge')
  async acknowledgeAlert(@Param('id') id: string, @Req() req: GuardedRequest) {
    await this.alerts.acknowledge(id, this.actor(req));
    return { acknowledged: true };
  }

  // ── Drills ─────────────────────────────────────────────────────────────────

  @Get('drills')
  async listDrills(@Query('targetId') targetId?: string) {
    const drills = await this.prisma.backupDrill.findMany({
      where: targetId ? { targetId } : {},
      orderBy: { startedAt: 'desc' },
      take: 50,
    });

    // Window coverage: the share of the advertised window that has actually
    // been restored from in the last 90 days. This is the figure that
    // substantiates the retention claim — a pass count alone cannot, because
    // passing the same recent-full drill twelve times proves one backup.
    const passed = drills.filter((d) => d.status === 'passed' && d.targetInstantAge !== null);
    const ages = passed.map((d) => d.targetInstantAge as number);
    const windowHours = 7 * 24;
    const buckets = new Set(ages.map((age) => Math.floor(age / 24)));
    const coveragePct = Math.round((buckets.size / (windowHours / 24)) * 100);

    return this.serialize({ drills, coveragePct: Math.min(coveragePct, 100) });
  }
}
